import { Loader2, RefreshCw, AlertTriangle, FileSpreadsheet } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { DashboardAlertsPanel } from '../dashboard/DashboardAlertsPanel';
import { DashboardImportDialog } from '../dashboard/DashboardImportDialog';
import { DashboardKpiGrid } from '../dashboard/DashboardKpiGrid';
import { DashboardPipelineCard } from '../dashboard/DashboardPipelineCard';
import { DashboardSubprojectsToWatch } from '../dashboard/DashboardSubprojectsToWatch';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { Button } from '../ui/button';
import { useDashboardAlerts, useDashboardOverview } from '../../services/dashboard';
import { useAppSettings } from '../../hook/useAppSettings';
import type { DashboardAlert } from '../../types/dashboard';

export function Dashboard() {
  const navigate = useNavigate();
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const { formatPercent } = useAppSettings();
  const {
    data: overview,
    loading: overviewLoading,
    error: overviewError,
    refresh: refreshOverview,
  } = useDashboardOverview();
  const {
    data: alertsData,
    loading: alertsLoading,
    error: alertsError,
    refresh: refreshAlerts,
  } = useDashboardAlerts();

  const isRefreshing = overviewLoading || alertsLoading;

  const handleRefresh = async () => {
    await Promise.allSettled([refreshOverview(), refreshAlerts()]);
  };

  const handleOpenAlert = (alert: DashboardAlert) => {
    navigate(alert.target_route);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="mb-2 text-2xl text-gray-900">Dashboard</h1>
          <p className="text-gray-600">Resumen operativo del estudio</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setImportDialogOpen(true)} disabled={isRefreshing}>
            <FileSpreadsheet className="mr-2 h-4 w-4" />
            Importar Excel
          </Button>
          <Button variant="outline" onClick={() => void handleRefresh()} disabled={isRefreshing}>
            {isRefreshing ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            Actualizar
          </Button>
        </div>
      </div>

      <DashboardKpiGrid loading={overviewLoading} summary={overview.summary} />

      {overviewError ? (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>No pudimos cargar el resumen del dashboard</AlertTitle>
          <AlertDescription>{overviewError}</AlertDescription>
        </Alert>
      ) : null}

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
        <div className="space-y-6 xl:col-span-6">
          <DashboardSubprojectsToWatch
            error={overviewError}
            items={overview.subprojects_to_watch}
            loading={overviewLoading}
            onOpen={(path) => navigate(path)}
            formatPercent={formatPercent}
          />
          <DashboardPipelineCard
            itemsByState={overview.items_by_state}
            ordersByStatus={overview.orders_by_status}
            projectsByStatus={overview.projects_by_status}
          />
        </div>

        <div className="xl:col-span-6">
          <DashboardAlertsPanel
            alerts={alertsData.items}
            error={alertsError}
            loading={alertsLoading}
            onOpen={handleOpenAlert}
          />
        </div>
      </div>

      <DashboardImportDialog
        open={importDialogOpen}
        onOpenChange={setImportDialogOpen}
        onCompleted={handleRefresh}
      />
    </div>
  );
}
